import type { DocumentPickerAsset } from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";

import { readUploadBody } from "./submissions";
import { supabase } from "./supabase";

export type LogoUploadFile = {
  uri: string;
  name: string;
  mimeType?: string;
  size?: number;
  file?: File;
};

const ALLOWED_MIME = new Set<string>([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
]);
const ALLOWED_EXT = new Set<string>(["png", "jpg", "jpeg", "webp", "svg"]);
const MAX_BYTES = 2 * 1024 * 1024;

function extFor(file: LogoUploadFile): string {
  if (file.name.includes(".")) {
    const e = file.name.split(".").pop()?.toLowerCase();
    if (e) return e === "jpeg" ? "jpg" : e;
  }
  switch (file.mimeType) {
    case "image/png":
      return "png";
    case "image/webp":
      return "webp";
    case "image/svg+xml":
      return "svg";
    case "image/jpeg":
    default:
      return "jpg";
  }
}

function assertAcceptable(file: LogoUploadFile): void {
  const mime = (file.mimeType ?? "").toLowerCase();
  const ext = file.name.includes(".") ? file.name.split(".").pop()?.toLowerCase() ?? "" : "";
  const mimeOk = mime ? ALLOWED_MIME.has(mime) : false;
  const extOk = ext ? ALLOWED_EXT.has(ext) : false;
  if (!mimeOk && !extOk) {
    throw new Error("Only PNG, JPG, WebP, SVG accepted.");
  }
  if (typeof file.size === "number" && file.size > MAX_BYTES) {
    throw new Error("Logo must be 2 MB or smaller.");
  }
}

export async function pickLogoFile(): Promise<LogoUploadFile | null> {
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ImagePicker.MediaTypeOptions.Images,
    allowsEditing: false,
    quality: 1,
  });
  if (result.canceled || !result.assets?.[0]) return null;
  const asset = result.assets[0];
  const filename = asset.fileName ?? `logo-${Date.now()}.png`;
  const size =
    typeof asset.fileSize === "number"
      ? asset.fileSize
      : (asset as { file?: { size?: number } }).file?.size;
  const picked: LogoUploadFile = {
    uri: asset.uri,
    name: filename,
    mimeType: asset.mimeType ?? "image/png",
    size,
    file: (asset as { file?: File }).file,
  };
  assertAcceptable(picked);
  return picked;
}

export async function uploadCompanyLogo(
  companyId: string,
  file: LogoUploadFile
): Promise<string> {
  assertAcceptable(file);

  const ext = extFor(file);
  const path = `${companyId}/logo.${ext}`;
  const { body, contentType: detectedType } = await readUploadBody(file as DocumentPickerAsset);
  const contentType = file.mimeType ?? detectedType ?? "image/png";

  // Clear any stale logo files in this company's folder under a different
  // extension so the public URL can be regenerated without ambiguity.
  const otherPaths = Array.from(ALLOWED_EXT)
    .map((e) => (e === "jpeg" ? "jpg" : e))
    .filter((e) => e !== ext)
    .map((e) => `${companyId}/logo.${e}`);
  if (otherPaths.length > 0) {
    await supabase.storage.from("company-logos").remove(otherPaths);
  }

  const { error } = await supabase.storage
    .from("company-logos")
    .upload(path, body, { upsert: true, contentType });
  if (error) throw new Error(error.message);

  const { data: publicData } = supabase.storage.from("company-logos").getPublicUrl(path);
  // Cache-bust so the existing img tag re-fetches; Storage's CDN caches by URL.
  const publicUrl = `${publicData.publicUrl}?v=${Date.now()}`;

  const { error: updateError } = await supabase
    .from("companies")
    .update({ logo_url: publicUrl })
    .eq("id", companyId);
  if (updateError) throw new Error(updateError.message);

  return publicUrl;
}

export async function removeCompanyLogo(companyId: string): Promise<void> {
  const paths = Array.from(ALLOWED_EXT)
    .map((e) => (e === "jpeg" ? "jpg" : e))
    .map((e) => `${companyId}/logo.${e}`);
  await supabase.storage.from("company-logos").remove(paths);

  const { error: updateError } = await supabase
    .from("companies")
    .update({ logo_url: null })
    .eq("id", companyId);
  if (updateError) throw new Error(updateError.message);
}
